// Opleidingen/cursussen DTOs — Spec D §16
//
// Vervangt het oude `Course`/`EmployeeCourse` model met een uitgebreid
// domein: opleiding → sessie → deelname → certificaat/skill.

import { z } from 'zod'
import { idParam } from './id-validators'
import { partialZonderDefaults } from './zod-patch'

// ─── Enums ───────────────────────────────────────────────────────────────────

export const OPLEIDING_CATEGORIEEN = [
  'VEILIGHEID',
  'VCA',
  'NEN3140',
  'HEFTRUCK',
  'PLC_TECHNIEK',
  'MECHANISCH',
  // FO-038 (batch-16) — Elektrotechniek, consistent met de ELEKTRO-discipline.
  'ELEKTROTECHNIEK',
  'LEIDINGGEVEN',
  'SOFT_SKILLS',
  'KLANTSPECIFIEK',
  'COMPLIANCE',
  'OVERIG',
] as const
export type OpleidingCategorie = (typeof OPLEIDING_CATEGORIEEN)[number]

export const OPLEIDING_CATEGORIE_LABELS: Record<OpleidingCategorie, string> = {
  VEILIGHEID: 'Veiligheid',
  VCA: 'VCA',
  NEN3140: 'NEN 3140',
  HEFTRUCK: 'Heftruck',
  PLC_TECHNIEK: 'PLC-techniek',
  MECHANISCH: 'Mechanisch',
  ELEKTROTECHNIEK: 'Elektrotechniek',
  LEIDINGGEVEN: 'Leidinggeven',
  SOFT_SKILLS: 'Soft skills',
  KLANTSPECIFIEK: 'Klantspecifiek',
  COMPLIANCE: 'Compliance',
  OVERIG: 'Overig',
}

export const OPLEIDING_TYPES = [
  'VEILIGHEID',
  'VAKTECHNISCH',
  'LEIDINGGEVEN',
  'PERSOONLIJK',
  'KLANTSPECIFIEK',
  'COMPLIANCE',
] as const
export type OpleidingType = (typeof OPLEIDING_TYPES)[number]

export const OPLEIDING_TYPE_LABELS: Record<OpleidingType, string> = {
  VEILIGHEID: 'Veiligheidsopleiding',
  VAKTECHNISCH: 'Vaktechnisch',
  LEIDINGGEVEN: 'Leidinggeven',
  PERSOONLIJK: 'Persoonlijk',
  KLANTSPECIFIEK: 'Klantspecifiek',
  COMPLIANCE: 'Compliance',
}

export const SESSIE_STATUSSEN = [
  'CONCEPT',
  'GEPUBLICEERD',
  'VOL',
  'LOPEND',
  'AFGEROND',
  'GEANNULEERD',
  // batch-63 cursusplanning (FO-468) - voorlopige sessie uit een cursusaanvraag.
  'AANGEVRAAGD',
] as const
export type SessieStatus = (typeof SESSIE_STATUSSEN)[number]

// FO-004 — sessies zijn puur interne backoffice-planning. De labels vermijden
// daarom "publiceren"/"opengesteld" (dat las alsof het iets in de medewerker-app
// opent). `GEPUBLICEERD` blijft als enum-/statuswaarde bestaan (geen migratie),
// maar leest als "Gepland (intern)".
export const SESSIE_STATUS_LABELS: Record<SessieStatus, string> = {
  CONCEPT: 'Concept',
  GEPUBLICEERD: 'Gepland (intern)',
  VOL: 'Vol',
  LOPEND: 'Lopend',
  AFGEROND: 'Afgerond',
  GEANNULEERD: 'Geannuleerd',
  AANGEVRAAGD: 'Aangevraagd (onder voorbehoud)',
}

/**
 * batch-63 cursusplanning (FO-468) - sessie-statussen die een voorlopige,
 * nog niet door de opleider bevestigde planning betekenen. Eén bron voor de
 * consumenten die zo'n sessie anders behandelen (herinneringen, afrond-signaal,
 * klant-afwezigheid, inschrijf-routes).
 */
export const SESSIE_STATUSSEN_VOORLOPIG = ['AANGEVRAAGD'] as const satisfies readonly SessieStatus[]

export function isVoorlopigeSessieStatus(status: string | null | undefined): boolean {
  return (SESSIE_STATUSSEN_VOORLOPIG as readonly string[]).includes(status ?? '')
}

export const DEELNAME_STATUSSEN = [
  'AANGEMELD',
  'INGEPLAND',
  'BEZIG',
  'AFGEROND',
  'NIET_GESLAAGD',
  'AFGEMELD',
  'NO_SHOW',
  'GEANNULEERD',
] as const
export type DeelnameStatus = (typeof DEELNAME_STATUSSEN)[number]

export const DEELNAME_STATUS_LABELS: Record<DeelnameStatus, string> = {
  AANGEMELD: 'Aangemeld',
  INGEPLAND: 'Ingepland',
  BEZIG: 'Bezig',
  AFGEROND: 'Afgerond',
  NIET_GESLAAGD: 'Niet geslaagd',
  AFGEMELD: 'Afgemeld',
  NO_SHOW: 'Niet verschenen',
  GEANNULEERD: 'Geannuleerd',
}

export const DEELNAME_RESULTATEN = [
  'GESLAAGD',
  'NIET_GESLAAGD',
  'DEELGENOMEN',
] as const
export type DeelnameResultaat = (typeof DEELNAME_RESULTATEN)[number]

export const DEELNAME_RESULTAAT_LABELS: Record<DeelnameResultaat, string> = {
  GESLAAGD: 'Geslaagd',
  NIET_GESLAAGD: 'Niet geslaagd',
  DEELGENOMEN: 'Deelgenomen',
}

export const AANVRAAG_STATUSSEN = [
  'AANGEVRAAGD',
  'IN_BEOORDELING',
  'GOEDGEKEURD',
  'INGEPLAND',
  'AFGEROND',
  'AFGEWEZEN',
] as const
export type AanvraagStatus = (typeof AANVRAAG_STATUSSEN)[number]

export const AANVRAAG_STATUS_LABELS: Record<AanvraagStatus, string> = {
  AANGEVRAAGD: 'Aangevraagd',
  IN_BEOORDELING: 'In beoordeling',
  GOEDGEKEURD: 'Goedgekeurd',
  INGEPLAND: 'Ingepland',
  AFGEROND: 'Afgerond',
  AFGEWEZEN: 'Afgewezen',
}

export const AANVRAAG_URGENTIEEN = [
  'LAAG',
  'NORMAAL',
  'HOOG',
  'VERPLICHT',
] as const
export type AanvraagUrgentie = (typeof AANVRAAG_URGENTIEEN)[number]

export const AANVRAAG_URGENTIE_LABELS: Record<AanvraagUrgentie, string> = {
  LAAG: 'Laag',
  NORMAAL: 'Normaal',
  HOOG: 'Hoog',
  VERPLICHT: 'Verplicht',
}

export const OPLEIDING_DOCUMENT_TYPES = [
  'LESMATERIAAL',
  'EXAMENRESULTAAT',
  'PRESENTIELIJST',
  'EVALUATIE',
  'HANDLEIDING',
  'CERTIFICAAT',
] as const
export type OpleidingDocumentType = (typeof OPLEIDING_DOCUMENT_TYPES)[number]

export const OPLEIDING_DOCUMENT_TYPE_LABELS: Record<OpleidingDocumentType, string> = {
  LESMATERIAAL: 'Lesmateriaal',
  EXAMENRESULTAAT: 'Examenresultaat',
  PRESENTIELIJST: 'Presentielijst',
  EVALUATIE: 'Evaluatie',
  HANDLEIDING: 'Handleiding',
  CERTIFICAAT: 'Certificaat',
}

// ─── Zod schemas ─────────────────────────────────────────────────────────────

export const OpleidingCategorieSchema = z.enum(OPLEIDING_CATEGORIEEN)
export const OpleidingTypeSchema = z.enum(OPLEIDING_TYPES)
export const SessieStatusSchema = z.enum(SESSIE_STATUSSEN)
export const DeelnameStatusSchema = z.enum(DEELNAME_STATUSSEN)
export const DeelnameResultaatSchema = z.enum(DEELNAME_RESULTATEN)
export const AanvraagStatusSchema = z.enum(AANVRAAG_STATUSSEN)
export const AanvraagUrgentieSchema = z.enum(AANVRAAG_URGENTIEEN)
export const OpleidingDocumentTypeSchema = z.enum(OPLEIDING_DOCUMENT_TYPES)

// ─── DTOs ────────────────────────────────────────────────────────────────────

export interface OpleidingDTO {
  id: string
  naam: string
  code: string | null
  omschrijving: string | null
  // FO-018 (batch-14) — optioneel vrij tekstveld "niveau".
  niveau: string | null
  categorie: OpleidingCategorie
  externeOpleider: string | null
  duurUren: number | null
  duurDagen: number | null
  geldigheidDagen: number | null
  herhalingDagen: number | null
  // FO-034 (batch-16) — `kostenPerDeelnemer` is EXCL. btw; `btwPercentage` is een
  // fractie (0.21 = 21%). Incl. btw leidt de UI/aggregatie af.
  kostenPerDeelnemer: number | null
  btwPercentage: number
  kostenToelichting: string | null
  maxDeelnemers: number | null
  locatie: string | null
  isIntern: boolean
  opleidingType: OpleidingType
  // FO-309 P4 — leidt deze opleiding tot een door de cursist zelf te uploaden
  // certificaat (extern examen)? Zo ja, "upload je certificaat"-melding bij afronden.
  leidtTotCertificaat: boolean
  // Spec NN §3.2.1A — default-opleider + default-locatie (cursus-cascade).
  opleiderId: string | null
  opleiderLocatieId: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface OpleidingSessieDagDTO {
  id: string
  sessieId: string
  datum: string
  startTijd: string | null
  eindTijd: string | null
  locatie: string | null
  opleiderLocatieId: string | null
}

export interface OpleidingSessieDTO {
  id: string
  opleidingId: string
  datum: string
  eindDatum: string | null
  startTijd: string | null
  eindTijd: string | null
  locatie: string | null
  docent: string | null
  docentEmail: string | null
  docentTelefoon: string | null
  maxDeelnemers: number | null
  status: SessieStatus
  kostenOverride: number | null
  // Spec NN §3.2.1A — per-sessie override op opleider + locatie.
  opleiderId: string | null
  opleiderLocatieId: string | null
  createdBy: string
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  dagen?: OpleidingSessieDagDTO[]
}

export interface OpleidingDeelnameDTO {
  id: string
  opleidingId: string
  sessieId: string | null
  employeeId: string
  status: DeelnameStatus
  resultaat: DeelnameResultaat | null
  certificaatNummer: string | null
  beoordeling: string | null
  score: number | null
  aangemeldDoor: string | null
  aangemeldOp: string
  afgerondOp: string | null
  kostenDeelnemer: number | null
  reiskosten: number | null
  verblijfkosten: number | null
  urenKosten: number | null
  notes: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface OpleidingAanvraagDTO {
  id: string
  employeeId: string
  opleidingId: string | null
  deelnameId: string | null
  motivatie: string
  urgentie: AanvraagUrgentie
  gewenstePeriode: string | null
  vrijevraag: string | null
  status: AanvraagStatus
  beoordeeldDoor: string | null
  beoordeeldOp: string | null
  beoordelingNotitie: string | null
  afwijzingsReden: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface OpleidingCertificaatKoppelingDTO {
  id: string
  opleidingId: string
  certificateTypeId: string
  autoActiveer: boolean
}

export interface OpleidingSkillKoppelingDTO {
  id: string
  opleidingId: string
  skillId: string
  doelNiveau: number | null
}

export interface SessieKalenderLinkDTO {
  id: string
  sessieId: string
  kalenderEventId: string
  employeeId: string
}

export interface OntwikkelPadDTO {
  id: string
  naam: string
  omschrijving: string | null
  doelFunctie: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface OpleidingPadStapDTO {
  id: string
  padId: string
  opleidingId: string
  volgorde: number
  isVerplicht: boolean
}

export interface MedewerkerOntwikkelPadDTO {
  id: string
  employeeId: string
  padId: string
  startDatum: string
  streefDatum: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface OpleidingDocumentDTO {
  id: string
  opleidingId: string | null
  sessieId: string | null
  deelnameId: string | null
  documentType: OpleidingDocumentType
  fileName: string
  filePath: string
  fileSize: number
  mimeType: string
  createdAt: string
  deletedAt: string | null
}

// ─── Composite DTOs (dashboard / voortgang) ──────────────────────────────────

export interface OntwikkelPadVoortgang {
  padId: string
  padNaam: string
  doelFunctie: string | null
  totaalStappen: number
  afgerondStappen: number
  voortgangPercentage: number
  stappen: Array<{
    volgorde: number
    opleidingNaam: string
    isVerplicht: boolean
    status: DeelnameStatus | 'NIET_GESTART'
    deelnameId: string | null
  }>
}

export interface OpleidingDashboardDTO {
  kpis: {
    actieveOpleidingen: number
    geplandeSessies: number
    deelnemersDezeKwartaal: number
    certificatenBijnaVerlopen: number
    openAanvragen: number
    slagingspercentage: number
    totaleKosten: number
  }
  komendeSessies: Array<{
    id: string
    opleidingNaam: string
    datum: string
    locatie: string | null
    aantalDeelnemers: number
    maxDeelnemers: number | null
  }>
  urgenteCertificaten: Array<{
    employeeId: string
    medewerkerNaam: string
    certificaatNaam: string
    verlooptOp: string
    status: 'BIJNA_VERLOPEN' | 'VERLOPEN'
  }>
}

export interface KostenOverzichtDTO {
  totaal: number
  perGroepering: Array<{
    label: string
    cursusgeld: number
    reiskosten: number
    verblijfkosten: number
    urenKosten: number
    totaal: number
    aantalDeelnames: number
  }>
  perKwartaal: Array<{
    kwartaal: string
    totaal: number
  }>
}

// ─── Input schemas (Spec D §SS4 — §SS6) ──────────────────────────────────────

export const CreateOpleidingSchema = z.object({
  naam: z.string().trim().min(1).max(200),
  code: z.string().trim().min(1).max(20).optional(),
  omschrijving: z.string().trim().max(2000).optional(),
  // FO-018 (batch-14) — optioneel vrij tekstveld "niveau" (bijv. "MBO 3", "Gevorderd").
  niveau: z.string().trim().max(100).optional(),
  categorie: OpleidingCategorieSchema.default('OVERIG'),
  opleidingType: OpleidingTypeSchema.default('VAKTECHNISCH'),
  externeOpleider: z.string().trim().max(200).optional(),
  duurUren: z.number().positive().max(10000).optional(),
  duurDagen: z.number().int().positive().max(365).optional(),
  geldigheidDagen: z.number().int().positive().max(36500).optional(),
  herhalingDagen: z.number().int().positive().max(36500).optional(),
  kostenPerDeelnemer: z.number().nonnegative().optional(),
  // FO-034 (batch-16) — btw als FRACTIE (0.21 = 21%), niet als percentage (21).
  // Optioneel: bij create vult de DB-default (0.21) aan; de UI stuurt 'm altijd mee.
  btwPercentage: z.number().min(0).max(1).optional(),
  kostenToelichting: z.string().trim().max(1000).optional(),
  maxDeelnemers: z.number().int().positive().max(1000).optional(),
  locatie: z.string().trim().max(200).optional(),
  isIntern: z.boolean().default(false),
  // FO-309 P4 — leidt tot een zelf te uploaden certificaat (extern examen).
  leidtTotCertificaat: z.boolean().default(false),
  // Spec NN §3.2.1A — default-opleider + default-locatie. `.nullable()` zodat een
  // edit-form die `null` terugkaatst (loskoppelen) geen ZodError geeft.
  opleiderId: idParam('opleiderId').nullable().optional(),
  opleiderLocatieId: idParam('opleiderLocatieId').nullable().optional(),
})
export type CreateOpleidingInput = z.infer<typeof CreateOpleidingSchema>

export const UpdateOpleidingSchema = partialZonderDefaults(CreateOpleidingSchema).extend({
  isActive: z.boolean().optional(),
})
export type UpdateOpleidingInput = z.infer<typeof UpdateOpleidingSchema>

// ─── FO-006 §8 — sessie-tijd-validatie (kwartier + start < eind) ──────────────
// Eén bron voor de tijd-regels van het sessie-planscherm: tijden vallen op het
// kwartier (00/15/30/45, conform de werkbon-tijdregel) en start ligt vóór eind.
// Gedeeld door de API-schema-refine (server) en de client-side form-validatie in
// het planscherm, zodat het form nooit een tijd stuurt die de API weigert.

const TIJD_FORMAAT = /^\d{2}:\d{2}$/

export const TIJD_KWARTIER_MELDING =
  'Tijd moet op het kwartier vallen (00, 15, 30 of 45 minuten)'
export const TIJD_VOLGORDE_MELDING = 'De eindtijd moet na de starttijd liggen'

/** True als `tijd` een geldige HH:mm-tijd op het kwartier is (00/15/30/45 min). */
export function isTijdOpKwartier(tijd: string): boolean {
  if (!TIJD_FORMAAT.test(tijd)) return false
  const uren = Number(tijd.slice(0, 2))
  const minuten = Number(tijd.slice(3, 5))
  return uren >= 0 && uren <= 23 && minuten <= 45 && minuten % 15 === 0
}

/** True als de starttijd strikt vóór de eindtijd ligt (geen gelijke/omgekeerde tijd). */
export function isStartVoorEind(start: string, eind: string): boolean {
  if (!TIJD_FORMAAT.test(start) || !TIJD_FORMAAT.test(eind)) return false
  const startMin = Number(start.slice(0, 2)) * 60 + Number(start.slice(3, 5))
  const eindMin = Number(eind.slice(0, 2)) * 60 + Number(eind.slice(3, 5))
  return startMin < eindMin
}

/**
 * Voegt de FO-006 §8-tijd-issues toe voor één tijd-paar (de hoofdtijd of één
 * dag): kwartier-check per veld + start < eind. `pad` is het zod-pad-prefix
 * (`[]` voor de hoofdtijd, `['dagen', i]` voor een afwijkende dag).
 */
function voegSessieTijdIssuesToe(
  ctx: z.RefinementCtx,
  start: string | null | undefined,
  eind: string | null | undefined,
  pad: Array<string | number>,
): void {
  if (start && !isTijdOpKwartier(start)) {
    ctx.addIssue({ code: 'custom', path: [...pad, 'startTijd'], message: TIJD_KWARTIER_MELDING })
  }
  if (eind && !isTijdOpKwartier(eind)) {
    ctx.addIssue({ code: 'custom', path: [...pad, 'eindTijd'], message: TIJD_KWARTIER_MELDING })
  }
  if (
    start &&
    eind &&
    isTijdOpKwartier(start) &&
    isTijdOpKwartier(eind) &&
    !isStartVoorEind(start, eind)
  ) {
    ctx.addIssue({ code: 'custom', path: [...pad, 'eindTijd'], message: TIJD_VOLGORDE_MELDING })
  }
}

// Basis-object zonder refine, zodat `UpdateOpleidingSessieSchema` (PATCH sessie,
// buiten FO-006-scope) `.partial()` kan blijven aanroepen — een ZodEffects (het
// resultaat van `.superRefine`) heeft die methode niet.
const CreateOpleidingSessieObject = z.object({
  datum: z.string().datetime(),
  eindDatum: z.string().datetime().optional(),
  startTijd: z
    .string()
    .regex(TIJD_FORMAAT, 'Verwacht HH:mm')
    .optional(),
  eindTijd: z
    .string()
    .regex(TIJD_FORMAAT, 'Verwacht HH:mm')
    .optional(),
  locatie: z.string().trim().max(200).optional(),
  docent: z.string().trim().max(200).optional(),
  docentEmail: z.string().email().optional(),
  docentTelefoon: z.string().trim().max(40).optional(),
  maxDeelnemers: z.number().int().positive().max(1000).optional(),
  kostenOverride: z.number().nonnegative().optional(),
  // Spec NN §3.2.1A — per-sessie override op opleider + locatie (gekoppelde
  // pickers). `.nullable()` zodat loskoppelen via `null` geen ZodError geeft.
  opleiderId: idParam('opleiderId').nullable().optional(),
  opleiderLocatieId: idParam('opleiderLocatieId').nullable().optional(),
  employeeIds: z.array(z.string().min(1)).min(1).max(50).optional(),
  dagen: z
    .array(
      z.object({
        datum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Verwacht yyyy-MM-dd'),
        startTijd: z.string().regex(TIJD_FORMAAT, 'Verwacht HH:mm').optional(),
        eindTijd: z.string().regex(TIJD_FORMAAT, 'Verwacht HH:mm').optional(),
        locatie: z.string().trim().max(200).optional(),
        opleiderLocatieId: idParam('opleiderLocatieId').nullable().optional(),
      }),
    )
    .min(1)
    .max(60)
    .optional(),
})

// FO-006 §6/§8 — de create-flow (sessie-planscherm) dwingt de tijd-regels af op
// de hoofdtijd én elke per-dag afwijkende tijd. PATCH/herplan (detailscherm)
// vallen buiten deze FO en houden hun eigen, bestaande schema's.
export const CreateOpleidingSessieSchema = CreateOpleidingSessieObject.superRefine((val, ctx) => {
  voegSessieTijdIssuesToe(ctx, val.startTijd, val.eindTijd, [])
  val.dagen?.forEach((dag, i) =>
    voegSessieTijdIssuesToe(ctx, dag.startTijd, dag.eindTijd, ['dagen', i]),
  )
})
export type CreateOpleidingSessieInput = z.infer<typeof CreateOpleidingSessieSchema>

export const UpdateOpleidingSessieSchema = CreateOpleidingSessieObject.partial()
export type UpdateOpleidingSessieInput = z.infer<typeof UpdateOpleidingSessieSchema>

export const AddDeelnemersSchema = z.object({
  employeeIds: z.array(z.string().min(1)).min(1).max(50),
})
export type AddDeelnemersInput = z.infer<typeof AddDeelnemersSchema>

// Beoordeel-flow-fix (2026-07) — koppel een catalogus-opleiding aan een
// (vrije-tekst-)aanvraag. Gedeeld door PATCH /opleiding-aanvragen/:id (API)
// én de koppel-UI op de beoordeelpagina (client-side validatie), zodat het
// form nooit iets stuurt dat de API weigert (CLAUDE.md "Validatie").
export const KoppelAanvraagOpleidingSchema = z.object({
  opleidingId: idParam('opleidingId'),
})
export type KoppelAanvraagOpleidingInput = z.infer<typeof KoppelAanvraagOpleidingSchema>

export const AfrondDeelnameSchema = z.object({
  resultaat: DeelnameResultaatSchema,
  certificaatNummer: z.string().trim().max(50).optional(),
  beoordeling: z.string().trim().max(1000).optional(),
  score: z.number().min(0).max(100).optional(),
})
export type AfrondDeelnameInput = z.infer<typeof AfrondDeelnameSchema>

export const KoppelCertificaatSchema = z.object({
  certificateTypeId: z.string().min(1),
  autoActiveer: z.boolean().default(true),
})
export type KoppelCertificaatInput = z.infer<typeof KoppelCertificaatSchema>

export const KoppelSkillSchema = z.object({
  skillId: z.string().min(1),
  doelNiveau: z.number().int().min(1).max(5),
})
export type KoppelSkillInput = z.infer<typeof KoppelSkillSchema>

export const AnnuleerSessieSchema = z.object({
  reden: z.string().trim().max(500).optional(),
})
export type AnnuleerSessieInput = z.infer<typeof AnnuleerSessieSchema>

// Batch-8 opleiding — deelnemer vervangen (omplannen): de oude deelnemer wordt
// afgemeld (incl. opruimen van zijn cursus-werkbonnen) en `nieuweEmployeeId`
// wordt 1-op-1 ingepland. Gedeeld schema zodat de API en het form dezelfde vorm
// valideren (geen onterechte VALIDATION_ERROR).
export const VervangDeelnemerSchema = z.object({
  nieuweEmployeeId: idParam('nieuweEmployeeId'),
  reden: z.string().trim().max(500).optional(),
})
export type VervangDeelnemerInput = z.infer<typeof VervangDeelnemerSchema>

// Batch-6 voertuig-stream §2b — sessie herplannen: alle cursusdagen worden
// vervangen door de opgegeven nieuwe dagen; status gaat terug naar
// GEPUBLICEERD en de gegenereerde planbord-werkbonnen verhuizen mee.
export const HerplanSessieSchema = z.object({
  dagen: z
    .array(
      z.object({
        datum: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Verwacht yyyy-MM-dd'),
        startTijd: z.string().regex(/^\d{2}:\d{2}$/, 'Verwacht HH:mm').optional(),
        eindTijd: z.string().regex(/^\d{2}:\d{2}$/, 'Verwacht HH:mm').optional(),
        locatie: z.string().trim().max(200).optional(),
        opleiderLocatieId: idParam('opleiderLocatieId').nullable().optional(),
      }),
    )
    .min(1)
    .max(60),
  reden: z.string().trim().max(500).optional(),
})
export type HerplanSessieInput = z.infer<typeof HerplanSessieSchema>

// ─── Opleider + OpleiderLocatie (Spec NN §3.2.1A — PO-besluit 10) ────────────
//
// Cursuslocaties zijn in de praktijk eigendom van een opleider/instituut (ROVC,
// Arbocentrum, Mikrocentrum) dat meerdere vestigingen heeft. Tweelagig
// datamodel: Opleider (bedrijf) → OpleiderLocatie (1:N adressen). Vervangt het
// vrije-tekstveld `Opleiding.externeOpleider`.
//
// lat/lng spiegelen de codebase-conventie voor geocode-coördinaten (Prisma
// `Decimal(9,6)` → DTO `number | null`; zie targeting-map / reisuren).

export interface OpleiderLocatieDTO {
  id: string
  opleiderId: string
  naam: string
  street: string | null
  houseNumber: string | null
  postalCode: string | null
  city: string | null
  country: string | null
  latitude: number | null
  longitude: number | null
  placeId: string | null
  geocodedAt: string | null
  isPrimair: boolean
  /** batch-63 (FO-468) - eigen planning-mailadres; null = dat van de opleider. */
  contactEmail: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

export interface OpleiderDTO {
  id: string
  naam: string
  kvkNumber: string | null
  website: string | null
  contactNaam: string | null
  contactEmail: string | null
  contactTelefoon: string | null
  isIntern: boolean
  /** batch-63 (FO-468) - geboortedatum van de deelnemers mee in de aanvraagmail. */
  vereistGeboortedatum: boolean
  notes: string | null
  isActive: boolean
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  /** Optioneel meegeleverd door de detail-response; lijst gebruikt OpleiderListRowDTO. */
  locaties?: OpleiderLocatieDTO[]
}

/** Afgeslankte rij voor de opleider-lijstweergave. */
export interface OpleiderListRowDTO {
  id: string
  naam: string
  isIntern: boolean
  isActive: boolean
  /** batch-63 (FO-468) - het algemene planning-mailadres van de opleider. */
  contactEmail: string | null
  vereistGeboortedatum: boolean
  aantalLocaties: number
  locaties: OpleiderLocatieDTO[]
}

// ─── Opleider Zod input-schemas ───────────────────────────────────────────────
//
// Optionele velden zijn `.nullable()` zodat een edit-form die `null` terugkaatst
// geen ZodError geeft (bekende valkuil: `.optional()` zonder `.nullable()`).

export const opleiderCreateSchema = z.object({
  naam: z.string().trim().min(1).max(200),
  kvkNumber: z.string().trim().max(20).nullable().optional(),
  website: z.string().trim().max(200).nullable().optional(),
  contactNaam: z.string().trim().max(200).nullable().optional(),
  contactEmail: z.string().email().max(200).nullable().optional(),
  contactTelefoon: z.string().trim().max(40).nullable().optional(),
  isIntern: z.boolean().default(false),
  // batch-63 cursusplanning (FO-468) - AVG: default uit.
  vereistGeboortedatum: z.boolean().default(false),
  notes: z.string().trim().max(2000).nullable().optional(),
})
export type OpleiderCreateInput = z.infer<typeof opleiderCreateSchema>

// Zod 4: `.partial()` laat een `.default()` staan - een PATCH zónder het veld
// zou dan de default schrijven (vinkje stil uit). Daarom de booleans hier
// expliciet optioneel: weglaten = ongewijzigd, zoals de handler bedoelt.
export const opleiderUpdateSchema = partialZonderDefaults(opleiderCreateSchema).extend({
  isIntern: z.boolean().optional(),
  vereistGeboortedatum: z.boolean().optional(),
  isActive: z.boolean().optional(),
})
export type OpleiderUpdateInput = z.infer<typeof opleiderUpdateSchema>

export const opleiderLocatieCreateSchema = z.object({
  opleiderId: idParam('opleiderId'),
  naam: z.string().trim().min(1).max(200),
  street: z.string().trim().max(200).nullable().optional(),
  houseNumber: z.string().trim().max(20).nullable().optional(),
  postalCode: z.string().trim().max(20).nullable().optional(),
  city: z.string().trim().max(200).nullable().optional(),
  country: z.string().trim().max(100).nullable().optional(),
  placeId: z.string().trim().max(500).nullable().optional(),
  isPrimair: z.boolean().default(false),
  // batch-63 cursusplanning (FO-468) - eigen planning-mailadres van de vestiging.
  contactEmail: z.string().trim().email().max(200).nullable().optional(),
})
export type OpleiderLocatieCreateInput = z.infer<typeof opleiderLocatieCreateSchema>

// Update zonder opleiderId (de locatie verhuist niet naar een andere opleider).
export const opleiderLocatieUpdateSchema = partialZonderDefaults(
  opleiderLocatieCreateSchema.omit({ opleiderId: true }),
)
  .extend({
    // Zie opleiderUpdateSchema: zonder deze override degradeert elke PATCH
    // zonder `isPrimair` de hoofdlocatie.
    isPrimair: z.boolean().optional(),
    isActive: z.boolean().optional(),
  })
export type OpleiderLocatieUpdateInput = z.infer<typeof opleiderLocatieUpdateSchema>
